import React, {useState, useEffect} from "react";
import {Box, Button, Popover, Dialog, DialogTitle, DialogContent, Stack, Typography, Autocomplete, TextField, Tooltip, CircularProgress, Link} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {MobileDatePicker} from "@mui/x-date-pickers/MobileDatePicker";
import dayjs from "dayjs";
import {fmtDay, fmtMoneyShort, fmtSignedMoney, shortProvider, utcYesterday, isNilValue} from "./format";
import {TYPE, TABULAR, NEUTRAL, CONTROL_H, WARNING_TEXT} from "./tokens";

const MAX_DAYS_BACK = 30;
export const isActivePull = (r) => r && (r.status === "queued" || r.status === "running");

/** `Bid Tags · Sep 13 · +$45` / `no change` / `failed: …` — what a finished request did (no row counts: they mean nothing to the reader). */
export const pullOutcome = (r) => {
    if (!r) return "";
    const known = !isNilValue(r.revenue_before) && !isNilValue(r.revenue_after);
    const d = known ? Number(r.revenue_after) - Number(r.revenue_before) : 0;
    const moved = Math.abs(d) >= 0.05;
    if (r.status === "failed") {
        // A partial failure (one dead account of several) can still have written revenue.
        const partial = moved ? ` (${fmtSignedMoney(d)} written, now ${fmtMoneyShort(r.revenue_after)})` : "";
        return `failed${partial}${r.message ? `: ${r.message}` : ""}`;
    }
    if (r.status !== "done") return r.status === "running" ? "running…" : "queued";
    if (!known) return "done";
    return moved ? `${fmtSignedMoney(d)} (now ${fmtMoneyShort(r.revenue_after)})` : `no change (${fmtMoneyShort(r.revenue_after)})`;
};
export const pullLabel = (r) => `${shortProvider(r.provider)} · ${fmtDay(r.day)} · ${pullOutcome(r)}`;

/** Partner + day form. Pending partners (not reported yet) come first in the list. */
const ResyncForm = ({providers, pendingProviders = [], defaultProvider, defaultDay, onSubmit, onClose, busy, error, phone, recent = []}) => {
    const maxDate = dayjs(utcYesterday().format("YYYY-MM-DD"));
    const [provider, setProvider] = useState(defaultProvider || null);
    const [day, setDay] = useState(() => (defaultDay ? dayjs(defaultDay) : maxDate));
    useEffect(() => { setProvider(defaultProvider || null); }, [defaultProvider]);
    useEffect(() => { if (defaultDay) setDay(dayjs(defaultDay)); }, [defaultDay]);
    const valid = Boolean(provider) && day && day.isValid() && !day.isAfter(maxDate) && !day.isBefore(maxDate.subtract(MAX_DAYS_BACK, "day"));
    const pendingSet = new Set(pendingProviders);
    const options = [...providers.filter((p) => pendingSet.has(p)), ...providers.filter((p) => !pendingSet.has(p))];
    const Picker = phone ? MobileDatePicker : DatePicker;
    return (
        <>
            <Typography sx={{fontSize: 12.5, color: "text.secondary", mb: 1.5, lineHeight: 1.4}}>
                Pulls that partner's report for that day again. Takes about a minute; the panel refreshes when it is done.
            </Typography>
            <Stack spacing={1.5}>
                <Autocomplete size="small" options={options} value={provider} getOptionLabel={(v) => shortProvider(v)} onChange={(e, v) => setProvider(v)}
                              groupBy={pendingProviders.length ? (o) => (pendingSet.has(o) ? "Not reported yet" : "All partners") : undefined}
                              renderInput={(p) => <TextField {...p} label="Partner" inputProps={{...p.inputProps, "aria-label": "Partner to re-sync"}}/>}/>
                <Picker label="Closed day (UTC)" value={day} format={phone ? "MMM D" : "YYYY-MM-DD"} maxDate={maxDate} minDate={maxDate.subtract(MAX_DAYS_BACK, "day")}
                        onChange={(d) => setDay(d)} slotProps={{textField: {size: "small", fullWidth: true}}}/>
                {error && <Typography sx={{fontSize: 12.5, color: "error.main"}}>{error}</Typography>}
            </Stack>
            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{mt: 2}}>
                <Button size="small" onClick={onClose} sx={{textTransform: "none"}}>Cancel</Button>
                <Button size="small" variant="contained" disableElevation disabled={!valid || busy} sx={{textTransform: "none"}}
                        onClick={() => onSubmit(provider, day.format("YYYY-MM-DD"))}>
                    {busy ? "Requesting…" : "Re-sync"}
                </Button>
            </Stack>
            {recent.length > 0 && (
                <Box sx={{mt: 2, pt: 1.5, borderTop: `1px solid ${NEUTRAL.hairline}`}}>
                    <Typography sx={{...TYPE.eyebrow, mb: 0.5}}>Recent (24 h)</Typography>
                    {recent.slice(0, 6).map((r) => (
                        <Typography key={r.id} component="div" noWrap title={`${pullLabel(r)}${r.requested_by ? ` · by ${r.requested_by}` : ""}`}
                                    sx={{fontSize: 12, ...TABULAR, lineHeight: 1.6, color: r.status === "failed" ? "error.main" : isActivePull(r) ? "primary.main" : "text.secondary"}}>
                            {pullLabel(r)}{r.finished_at ? ` · ${dayjs(r.finished_at).format("HH:mm")}` : ""}
                        </Typography>
                    ))}
                </Box>
            )}
        </>
    );
};

/**
 * The re-sync entry point. `variant="button"` = a quiet text button (desktop header); `variant="link"` = an inline
 * "re-sync" link (pending line, feed header); `variant="none"` = no trigger, only the dialog for contextual prefills.
 * `prefill` = {provider, day} opens the form prefilled (contextual buttons); `recent` = requests of the last 24 h.
 */
export const ResyncButton = ({providers, pendingProviders, onRequest, phone = false, prefill = null, onPrefillConsumed, variant = "button", label = "Re-sync partner…", recent = [], sx}) => {
    const [anchor, setAnchor] = useState(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => { if (prefill) { setOpen(true); setError(null); } }, [prefill]);
    const close = () => { setOpen(false); setAnchor(null); setError(null); if (onPrefillConsumed) onPrefillConsumed(); };
    const submit = async (provider, day) => {
        setBusy(true);
        const res = await onRequest(provider, day);
        setBusy(false);
        if (res && res.success) close(); else setError((res && res.message) || "Could not queue the re-sync");
    };
    const openAt = (e) => { setError(null); if (!phone && e && e.currentTarget) setAnchor(e.currentTarget); setOpen(true); };
    const form = <ResyncForm providers={providers} pendingProviders={pendingProviders} defaultProvider={prefill ? prefill.provider : null} defaultDay={prefill ? prefill.day : null}
                             onSubmit={submit} onClose={close} busy={busy} error={error} phone={phone} recent={recent}/>;
    const trigger = variant === "none" ? null : variant === "link" ? (
        <Link component="button" type="button" underline="hover" onClick={openAt} aria-label="Re-sync a partner for one day"
              sx={{fontSize: "inherit", fontFamily: "inherit", fontWeight: 600, verticalAlign: "baseline", ...sx}}>{label}</Link>
    ) : (
        <Tooltip title="Pull a partner's report for one day again (missing or incomplete rows)" arrow enterDelay={400}>
            <Button size="small" variant="text" color="inherit" aria-label="Re-sync a partner for one day" onClick={openAt}
                    sx={{height: CONTROL_H, textTransform: "none", fontSize: 12.5, fontWeight: 600, color: "text.secondary", px: 1, whiteSpace: "nowrap", ...sx}}>
                {label}
            </Button>
        </Tooltip>
    );
    return (
        <>
            {trigger}
            {phone ? (
                <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
                    <DialogTitle sx={{fontSize: 16, fontWeight: 600, pb: 1}}>Re-sync a partner</DialogTitle>
                    <DialogContent sx={{pt: "8px !important"}}>{form}</DialogContent>
                </Dialog>
            ) : (
                <Popover open={open} anchorEl={anchor || undefined} onClose={close} anchorReference={anchor ? "anchorEl" : "anchorPosition"}
                         anchorPosition={anchor ? undefined : {top: 96, left: Math.max(380, (typeof window !== "undefined" ? window.innerWidth : 1200) - 24)}}
                         anchorOrigin={{vertical: "bottom", horizontal: "right"}} transformOrigin={{vertical: "top", horizontal: "right"}}
                         PaperProps={{sx: {p: 2, mt: 0.5, width: 360}}}>
                    <Typography sx={{...TYPE.eyebrow, mb: 1}}>Re-sync a partner</Typography>
                    {form}
                </Popover>
            )}
        </>
    );
};

/** One 32 px line, only while a request is queued / running: `Re-syncing Bid Tags · Sep 13… (+1 queued)`. */
export const PullBanner = ({requests, sx}) => {
    const active = (requests || []).filter(isActivePull);
    if (!active.length) return null;
    const first = active.find((r) => r.status === "running") || active[0];
    return (
        <Box role="status" sx={{display: "flex", alignItems: "center", gap: 1, height: 32, px: 1.5, fontSize: 12.5, ...TABULAR, color: "text.secondary",
            bgcolor: "rgba(25,118,210,.05)", borderBottom: `1px solid ${NEUTRAL.hairline}`, ...sx}}>
            <CircularProgress size={12}/>
            <Typography component="span" noWrap sx={{fontSize: "inherit", minWidth: 0}}>
                Re-syncing <Box component="span" sx={{color: "text.primary", fontWeight: 600}}>{shortProvider(first.provider)} · {fmtDay(first.day)}</Box>…
                {active.length > 1 ? ` (+${active.length - 1} queued)` : ""}
            </Typography>
        </Box>
    );
};

export {WARNING_TEXT as PULL_WARNING};
